/**
 * HomepageRedesign — landing v3 (liquid glass + depth + layers).
 *
 * Audience toggle (creator | brand):
 *   - Pill segmented control sits above the announcement strip in the hero.
 *   - Creator is on the LEFT and is the DEFAULT.
 *   - Toggling rewrites copy across Hero/Features/HowItWorks/SocialProof/CTA
 *     and switches CTA destinations between /auth?type=creator and /auth?type=brand.
 *   - Same liquid-glass aesthetic for both audiences. Creator copy is intentionally
 *     simpler and more direct (earnings-first), brand copy keeps the outcomes framing.
 *
 * Sections:
 *   - Hero: floating layered glass stage (HeroLiquidStage)
 *   - Features: rich liquid-glass cards with cursor-following glow
 *   - HowItWorks: numbered glass tiles with chromatic edges
 *   - SocialProof: floating glass kpi tiles, count-up
 *   - CTA: aurora glow + magnetic primary
 */
import * as React from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import {
  ArrowRight,
  Check,
  Play,
  Zap,
  Shield,
  BarChart3,
  Wallet,
  LayoutDashboard,
  TrendingUp,
  Sparkles,
  CircleDollarSign,
  Users,
} from "lucide-react";
import { MarketingLayout } from "@/redesign/layouts";
import { RdButton } from "@/redesign/components";
import { useAuth } from "@/hooks/useAuth";
import { useAudienceMode } from "@/hooks/useAudienceMode";
import { MEyebrow, MTitle, MLede, MSection, MSectionTitle } from "./_components";
import { HeroLiquidStage } from "./HeroLiquidStage";
import { CreatorLiquidStage } from "./CreatorLiquidStage";
import { LiquidGlass, LiquidGlassDefs, GrainOverlay } from "./LiquidGlass";

/* ----------------------------------------------------------------- */
/* Audience model                                                      */
/* ----------------------------------------------------------------- */
type Audience = "creator" | "brand";

const SIGNUP_URL: Record<Audience, string> = {
  creator: "/auth?type=creator",
  brand: "/auth?type=brand",
};

/** All audience-specific copy lives here so the rest of the file stays clean. */
const COPY = {
  creator: {
    announce: "Open briefs on TikTok, Instagram & YouTube Shorts",
    eyebrow: "Get paid per verified view",
    titleLead: "The content you’re already making",
    titleAccent: "can pay you",
    titleTail: ".",
    lede:
      "Pick a brief. Post the content your way. Get paid per 1,000 verified views — every Friday, straight to your bank.",
    primaryCta: "Start earning",
    secondaryCta: "Browse open briefs",
    secondaryHref: "/auth?type=creator",
    checks: [
      "Free to join",
      "No follower minimum",
      "Paid every Friday",
    ],
    featuresEyebrow: "Built for creators",
    featuresTitle: "Real briefs. Real money. Zero gatekeepers.",
    features: [
      {
        icon: Zap,
        title: "See briefs before you commit",
        desc: "CPM, budget, and dos/don’ts up front. No DMs, no applications, no \"we'll get back to you.\"",
      },
      {
        icon: TrendingUp,
        title: "Every view earns",
        desc: "Every 1,000 verified views drops cash in your wallet. The more your content lands, the more you take home.",
      },
      {
        icon: CircleDollarSign,
        title: "Friday payouts, your way",
        desc: "ACH, debit card, or stablecoin — pick how you cash out. No invoices, no chasing brand reps.",
      },
      {
        icon: Shield,
        title: "You always see your money",
        desc: "Live earnings, live views, next payout date. No guessing whether a brand actually pays.",
      },
    ],
    howEyebrow: "How it works",
    howTitle: "Three steps. From sign-up to paid Friday.",
    steps: [
      {
        n: "01",
        t: "Grab a brief",
        d: "Pick from open briefs. CPM and rules are right there — no fine print.",
      },
      {
        n: "02",
        t: "Post your content",
        d: "Your account, your style. Drop the link, we count the verified views.",
      },
      {
        n: "03",
        t: "Get paid Friday",
        d: "Earnings settle weekly. ACH, debit, or stablecoin — your call.",
      },
    ],
    proofEyebrow: "Creator story",
    proofQuote:
      "“Made more in two weekends with CreatorVault than I did in three months of brand DMs. I just pick a brief and post.”",
    proofAuthor: "— Maya R., 142k on TikTok",
    proofCta: "Read more creator stories",
    // Creator-personal proof tiles — what a creator wants to know about
    // joining the platform, not platform-wide aggregates.
    proofStats: [
      { l: "Highest payout this month", v: 14_280, prefix: "$" },
      { l: "Avg earnings · top 10%", v: 4_120, prefix: "$", suffix: "/mo" },
      { l: "Open briefs right now", v: 87 },
      { l: "Days to first payout", v: 7 },
    ],
    finalTitle: "Your next payout is one post away.",
    finalLede:
      "Free to join. No follower minimum. Your first verified payout is on us — even if it’s your first post.",
    finalPrimary: "Start earning",
    finalSecondary: "How payouts work",
    finalSecondaryHref: "/docs",
  },
  brand: {
    announce: "3-day free trial on every plan — cancel anytime, no charge for 3 days",
    eyebrow: "Pay creators per verified view",
    titleLead: "The creator-marketing platform for",
    titleAccent: "outcomes",
    titleTail: ", not promises.",
    lede:
      "Launch a campaign in minutes. Get hundreds of clips from real creators. Pay only for the views that move — verified at the platform layer.",
    primaryCta: "Start 3-day free trial",
    secondaryCta: "Book a demo",
    secondaryHref: "/contact",
    // "No credit card required" was factually wrong — checkout now requires a
    // card so Stripe can auto-bill at trial end (commit 8980650e). Card is
    // saved, not charged, during the 3 free days.
    checks: ["3 days free, cancel anytime", "Live in 5 minutes", "SOC 2 Type II"],
    featuresEyebrow: "Why CreatorVault",
    featuresTitle: "The growth engine that pays for itself.",
    features: [
      {
        icon: Zap,
        title: "Launch in minutes",
        desc: "Set a budget, brief, and CPM. Creators apply, you approve, clips ship.",
      },
      {
        icon: BarChart3,
        title: "Verified analytics",
        desc: "Per-clip views, engagement, and ROAS — pulled directly from TikTok and Instagram.",
      },
      {
        icon: Wallet,
        title: "Outcome-based payouts",
        desc: "Pay per 1,000 verified views. No retainers, no fixed fees, no surprises.",
      },
      {
        icon: Shield,
        title: "Brand-safe by default",
        desc: "Every clip reviewed for guidelines before payout. Disputes handled in-platform.",
      },
    ],
    howEyebrow: "How it works",
    howTitle: "Three steps. No agencies. No retainers.",
    steps: [
      {
        n: "01",
        t: "Brief your campaign",
        d: "Budget, target CPM, do’s and don’ts. Save as template.",
      },
      {
        n: "02",
        t: "Approve creators",
        d: "Review applications with portfolio + audience data. Approve in one click.",
      },
      {
        n: "03",
        t: "Pay per view",
        d: "Verified views feed payouts. ACH, wire, or stablecoin — your call.",
      },
    ],
    proofEyebrow: "Customer story",
    proofQuote:
      "“4× ROAS in week one. Nothing else comes close. We launched on Tuesday and had 12M verified views by Friday.”",
    proofAuthor: "— Dani K., Head of Growth at NorthLine",
    proofCta: "Read the story",
    proofStats: [
      { l: "Verified views", v: 12_400_000, short: true },
      { l: "ROAS", v: 4.1, suffix: "×", decimals: 1 },
      { l: "Clips delivered", v: 284 },
      { l: "Time to launch", v: 8, suffix: " min" },
    ],
    finalTitle: "Spin up your first campaign today.",
    finalLede:
      "Start with a 3-day free trial — cancel anytime, no charge for 3 days. Then pay only for verified views.",
    finalPrimary: "Start 3-day free trial",
    finalSecondary: "View pricing",
    finalSecondaryHref: "/pricing",
  },
} as const;

/* ----------------------------------------------------------------- */
/* Reveal wrapper                                                      */
/* ----------------------------------------------------------------- */
function Reveal({
  children,
  delay = 0,
  y = 14,
  className,
}: React.PropsWithChildren<{ delay?: number; y?: number; className?: string }>) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -8% 0px" }}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ----------------------------------------------------------------- */
/* Magnetic CTA                                                        */
/* ----------------------------------------------------------------- */
function Magnetic({
  children,
  strength = 0.28,
}: React.PropsWithChildren<{ strength?: number }>) {
  const reduce = useReducedMotion();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.4 });

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * strength);
    y.set((e.clientY - r.top - r.height / 2) * strength);
  }
  function onLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={reduce ? undefined : { x: sx, y: sy }}
      className="inline-block"
    >
      {children}
    </motion.div>
  );
}

/* ----------------------------------------------------------------- */
/* Count-up (IntersectionObserver-triggered)                           */
/* ----------------------------------------------------------------- */
function CountUp({
  to,
  prefix = "",
  suffix = "",
  decimals = 0,
  short = false,
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  short?: boolean;
}) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const reduce = useReducedMotion();

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const fmt = (v: number) => {
      if (short) {
        if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
        if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
      }
      return v.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    };
    if (reduce) {
      node.textContent = `${prefix}${fmt(to)}${suffix}`;
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const start = performance.now();
            const dur = 1600;
            const ease = (t: number) => 1 - Math.pow(1 - t, 3);
            const tick = (now: number) => {
              const t = Math.min(1, (now - start) / dur);
              const v = to * ease(t);
              node.textContent = `${prefix}${fmt(v)}${suffix}`;
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [to, prefix, suffix, decimals, short, reduce]);

  return <span ref={ref}>{prefix}0{suffix}</span>;
}

/* ----------------------------------------------------------------- */
/* AudienceToggle — segmented liquid-glass pill                        */
/* ----------------------------------------------------------------- */
function AudienceToggle({
  audience,
  onChange,
}: {
  audience: Audience;
  onChange: (a: Audience) => void;
}) {
  const reduce = useReducedMotion();
  return (
    <div className="flex justify-center">
      <div
        role="tablist"
        aria-label="Choose your view"
        className="relative inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.03] p-1 backdrop-blur-md"
        style={{
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 28px -16px hsl(var(--rd-accent-500) / 0.45)",
        }}
      >
        {(["creator", "brand"] as const).map((a) => {
          const selected = audience === a;
          const Icon = a === "creator" ? Sparkles : Users;
          return (
            <button
              key={a}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(a)}
              className={[
                "relative z-10 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5",
                "text-[13px] font-medium transition-colors duration-300",
                selected
                  ? "text-[hsl(var(--rd-text-1))]"
                  : "text-[hsl(var(--rd-text-3))] hover:text-[hsl(var(--rd-text-2))]",
              ].join(" ")}
            >
              {selected && (
                <motion.span
                  layoutId="audience-toggle-pill"
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "linear-gradient(160deg, hsl(var(--rd-accent-500) / 0.32) 0%, hsl(var(--rd-accent-700) / 0.22) 100%)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.18), 0 0 0 1px hsl(var(--rd-accent-500) / 0.35)",
                  }}
                  transition={
                    reduce
                      ? { duration: 0 }
                      : { type: "spring", stiffness: 380, damping: 32 }
                  }
                />
              )}
              <Icon className="relative h-3.5 w-3.5" aria-hidden />
              <span className="relative">
                {a === "creator" ? "Creator" : "Brand"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================= */
/* Page                                                                */
/* ================================================================= */
export default function HomepageRedesign() {
  const { user, profile, loading } = useAuth();
  const isSignedIn = !!user && !!profile && !loading;
  const [audience, setAudience] = useAudienceMode();

  return (
    <>
      <LiquidGlassDefs />
      <GrainOverlay opacity={0.05} />
      <MarketingLayout
        cta={
          isSignedIn ? (
            <RdButton size="sm" asChild>
              <Link to="/dashboard" className="inline-flex items-center gap-1.5">
                <LayoutDashboard className="h-3.5 w-3.5" />
                Dashboard
              </Link>
            </RdButton>
          ) : (
            <>
              <Link
                to="/auth"
                className="rounded-md px-3 py-1.5 text-[13.5px] font-medium text-[hsl(var(--rd-text-2))] hover:text-[hsl(var(--rd-text-1))]"
              >
                Log in
              </Link>
              <RdButton size="sm" asChild>
                <Link to={SIGNUP_URL[audience]}>Get started</Link>
              </RdButton>
            </>
          )
        }
      >
        <Hero audience={audience} setAudience={setAudience} />
        <Features audience={audience} />
        <HowItWorks audience={audience} />
        <SocialProof audience={audience} />
        <CTA audience={audience} />
      </MarketingLayout>
    </>
  );
}

/* ================================================================= */
/* Hero                                                                */
/* ================================================================= */
function Hero({
  audience,
  setAudience,
}: {
  audience: Audience;
  setAudience: (a: Audience) => void;
}) {
  const c = COPY[audience];
  return (
    <section className="relative overflow-hidden">
      <Atmosphere />

      {/* Grid hairlines, masked */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,hsl(var(--rd-border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--rd-border))_1px,transparent_1px)] [background-size:48px_48px] opacity-[0.10] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      />

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-20 md:pt-24">
        {/* Audience toggle — Creator on the LEFT and DEFAULT */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          <AudienceToggle audience={audience} onChange={setAudience} />
        </motion.div>

        {/* Pill announcement */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
          className="mt-7 flex justify-center"
        >
          <Link
            to="/blog"
            className="group inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1 text-[12px] text-[hsl(var(--rd-text-2))] backdrop-blur-md transition hover:border-[hsl(var(--rd-accent-500))]/40 hover:text-[hsl(var(--rd-text-1))]"
          >
            <span className="rounded-full bg-[hsl(var(--rd-accent-500))]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--rd-accent-300))]">
              New
            </span>
            {c.announce}
            <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
          </Link>
        </motion.div>

        {/* All audience-aware copy fades on switch */}
        <motion.div
          key={audience}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <MEyebrow className="mt-7">{c.eyebrow}</MEyebrow>

          <MTitle className="mx-auto mt-5">
            {c.titleLead}
            <br className="hidden md:inline" />{" "}
            <span className="relative inline-block">
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(110deg, hsl(var(--rd-accent-200)) 0%, hsl(var(--rd-accent-500)) 45%, hsl(var(--rd-accent-300)) 100%)",
                }}
              >
                {c.titleAccent}
              </span>
              <motion.span
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
                className="absolute -bottom-1 left-0 right-0 h-[2px] origin-left bg-gradient-to-r from-transparent via-[hsl(var(--rd-accent-500))]/80 to-transparent"
              />
            </span>
            {c.titleTail}
          </MTitle>

          <MLede className="mx-auto mt-6">{c.lede}</MLede>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Magnetic>
              <RdButton size="lg" asChild>
                <Link to={SIGNUP_URL[audience]}>
                  {c.primaryCta} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </RdButton>
            </Magnetic>
            <RdButton size="lg" variant="outline" asChild>
              <Link to={c.secondaryHref}>
                {audience === "brand" && (
                  <Play className="mr-1.5 h-3.5 w-3.5" />
                )}
                {c.secondaryCta}
              </Link>
            </RdButton>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-[hsl(var(--rd-text-3))]">
            {c.checks.map((label) => (
              <span key={label} className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-[hsl(var(--rd-success))]" />{" "}
                {label}
              </span>
            ))}
          </div>
        </motion.div>

        {/* The liquid-glass layered stage — switches between creator and brand */}
        <div className="relative mt-14 md:mt-20">
          {audience === "creator" ? <CreatorLiquidStage /> : <HeroLiquidStage />}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- */
/* Atmosphere — animated mesh + vignette + bottom glow                 */
/* ----------------------------------------------------------------- */
function Atmosphere() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <motion.div
        className="absolute -top-32 -left-24 h-[560px] w-[560px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-500) / 0.32) 0%, transparent 60%)",
        }}
        animate={{ x: [0, 70, -20, 0], y: [0, 40, -30, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-10 right-[-180px] h-[500px] w-[500px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-400) / 0.24) 0%, transparent 60%)",
        }}
        animate={{ x: [0, -50, 30, 0], y: [0, 30, -20, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-[460px] left-1/2 h-[380px] w-[380px] -translate-x-1/2 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-300) / 0.20) 0%, transparent 60%)",
        }}
        animate={{ x: [-20, 30, -10, -20], y: [0, -20, 20, 0] }}
        transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Bottom vignette to anchor */}
      <div
        className="absolute inset-x-0 bottom-0 h-48"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, hsl(var(--rd-canvas)) 100%)",
        }}
      />
    </div>
  );
}

/* ================================================================= */
/* Features                                                            */
/* ================================================================= */
function Features({ audience }: { audience: Audience }) {
  const c = COPY[audience];
  return (
    <MSection>
      <Reveal>
        <MEyebrow>{c.featuresEyebrow}</MEyebrow>
        <MSectionTitle className="mt-3">{c.featuresTitle}</MSectionTitle>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2">
        {c.features.map((it, i) => (
          <Reveal key={`${audience}-${it.title}`} delay={0.06 * i}>
            <FeatureCard icon={it.icon} title={it.title} desc={it.desc} />
          </Reveal>
        ))}
      </div>
    </MSection>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  desc,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const [glow, setGlow] = React.useState({ x: 50, y: 50, on: false });

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setGlow({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
      on: true,
    });
  }
  function onLeave() {
    setGlow((g) => ({ ...g, on: false }));
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileHover={reduce ? undefined : { y: -3 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      className="group relative"
    >
      <LiquidGlass radius={18} elevation="mid" className="p-7">
        {/* Liquid glow that follows the cursor */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[18px] transition-opacity duration-300"
          style={{
            background: `radial-gradient(440px circle at ${glow.x}% ${glow.y}%, hsl(var(--rd-accent-500) / 0.16), transparent 45%)`,
            opacity: glow.on ? 1 : 0,
          }}
        />
        <div className="relative">
          <div
            className="inline-flex h-10 w-10 items-center justify-center rounded-[12px] text-[hsl(var(--rd-accent-200))]"
            style={{
              background:
                "linear-gradient(160deg, hsl(var(--rd-accent-500) / 0.28) 0%, hsl(var(--rd-accent-700) / 0.18) 100%)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.25), 0 0 0 1px hsl(var(--rd-accent-500) / 0.25)",
            }}
          >
            <Icon className="h-[18px] w-[18px]" />
          </div>
          <div className="mt-5 text-[19px] font-semibold tracking-tight text-[hsl(var(--rd-text-1))]">
            {title}
          </div>
          <p className="mt-1.5 text-[14px] leading-relaxed text-[hsl(var(--rd-text-2))]">
            {desc}
          </p>
        </div>
      </LiquidGlass>
    </motion.div>
  );
}

/* ================================================================= */
/* HowItWorks                                                          */
/* ================================================================= */
function HowItWorks({ audience }: { audience: Audience }) {
  const c = COPY[audience];
  return (
    <MSection className="bg-[hsl(var(--rd-surface-1))]/30">
      <Reveal>
        <MEyebrow>{c.howEyebrow}</MEyebrow>
        <MSectionTitle className="mt-3">{c.howTitle}</MSectionTitle>
      </Reveal>
      <div className="relative mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
        {c.steps.map((s, i) => (
          <Reveal key={`${audience}-${s.n}`} delay={0.08 * i}>
            <LiquidGlass radius={18} elevation="mid" className="h-full p-7">
              <div className="font-mono text-[13px] tabular-nums text-[hsl(var(--rd-accent-300))]">
                {s.n}
              </div>
              <div className="mt-3 text-[20px] font-semibold tracking-tight text-[hsl(var(--rd-text-1))]">
                {s.t}
              </div>
              <p className="mt-2 text-[14px] leading-relaxed text-[hsl(var(--rd-text-2))]">
                {s.d}
              </p>
            </LiquidGlass>
          </Reveal>
        ))}
      </div>
    </MSection>
  );
}

/* ================================================================= */
/* SocialProof                                                         */
/* ================================================================= */
function SocialProof({ audience }: { audience: Audience }) {
  const c = COPY[audience];
  return (
    <MSection>
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
        <Reveal>
          <MEyebrow>{c.proofEyebrow}</MEyebrow>
          <MSectionTitle className="mt-3">{c.proofQuote}</MSectionTitle>
          <div className="mt-4 text-[13px] text-[hsl(var(--rd-text-3))]">
            {c.proofAuthor}
          </div>
          <RdButton variant="ghost" className="mt-6" asChild>
            <Link to="/case-studies">
              {c.proofCta} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </RdButton>
        </Reveal>
        <div className="grid grid-cols-2 gap-3">
          {c.proofStats.map((s, i) => (
            <Reveal key={`${audience}-${s.l}`} delay={0.06 * i}>
              <LiquidGlass radius={18} elevation="mid" className="p-6">
                <div className="text-[11.5px] uppercase tracking-wider text-[hsl(var(--rd-text-3))]">
                  {s.l}
                </div>
                <div className="mt-2 font-mono text-[28px] font-semibold tabular-nums text-[hsl(var(--rd-text-1))]">
                  <CountUp
                    to={s.v}
                    prefix={"prefix" in s ? s.prefix : ""}
                    suffix={"suffix" in s ? s.suffix : ""}
                    decimals={"decimals" in s ? s.decimals : 0}
                    short={"short" in s ? s.short : false}
                  />
                </div>
              </LiquidGlass>
            </Reveal>
          ))}
        </div>
      </div>
    </MSection>
  );
}

/* ================================================================= */
/* CTA                                                                 */
/* ================================================================= */
function CTA({ audience }: { audience: Audience }) {
  const c = COPY[audience];
  return (
    <section className="relative overflow-hidden border-b border-[hsl(var(--rd-border))]/60">
      {/* Aurora glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 50%, hsl(var(--rd-accent-500) / 0.20), transparent 70%)",
        }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 left-1/2 h-80 w-[680px] -translate-x-1/2 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-400) / 0.32) 0%, transparent 60%)",
        }}
        animate={{ x: ["-50%", "-44%", "-56%", "-50%"], scale: [1, 1.05, 0.97, 1] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="mx-auto w-full max-w-3xl px-6 py-28 text-center">
        <Reveal>
          <MSectionTitle className="mx-auto">{c.finalTitle}</MSectionTitle>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-[hsl(var(--rd-text-2))]">
            {c.finalLede}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Magnetic>
              <RdButton size="lg" asChild>
                <Link to={SIGNUP_URL[audience]}>
                  {c.finalPrimary} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </RdButton>
            </Magnetic>
            <RdButton size="lg" variant="outline" asChild>
              <Link to={c.finalSecondaryHref}>{c.finalSecondary}</Link>
            </RdButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
